import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { SEC_TO_MS } from "cfg/const";
import { EditableAutosizedInput } from "components/EditableList";
import { Filter, FilterOption, FilterOption__STRICT, FilterOption__STRICT_MULTI_VLAUE as FilterOption__STRICT_MULTI_VALUE, FilterType } from "components/Filter";
import { Counterpart, COUNTERPART_TO_HUMAN_READABLE, NO_LOOKBACK_RULE, NO_PARTY_ROLE, PartyRole, Remark, REMARKS_ALLOWED, REMARKS_BEHAVIORS, REMARKS_OBJECTIONS, REMARKS_OUTCOMES, REMARK_TO_HUMAN_READABLE, STAGE, StageToHumanReadable } from "interfaces/db";
import { LookbackRule, TimeFilterType } from "interfaces/plays";
import { ProspectInfoChoices } from "interfaces/metadata";
import { Typography, TypographyVariant } from "interfaces/typography";
import React from "react";
import uuid from "react-uuid";
import DatePicker from "react-datepicker";
import './custom.css'
import NumericInput from "react-numeric-input";
import { PromptType, PromptTypeToHumanReadable } from "interfaces/consts";
import { UserPicker, UserPickerBucket, UserPickerImpl, UserPickerSelection, UserPickerSelectionType } from "components/UserPicker";
import { xIcon } from "icons";
import { connect } from "react-redux";
import { RootState } from "store";
import { Debouncer } from "../Debouncer";

export enum Option {
    PHONE_NUMBERS = 'PHONE_NUMBERS',
    CALL_CATEGORY = 'CALL_CATEGORY',
    USER_IDS = 'USER_IDS',
    REMARK = 'REMARK',
    TEXT = 'TEXT',
    STAGES = 'STAGE',
    COUNTERPARTS='COUNTERPARTIES',
    PROMPT_TYPES='PROMPT_TYPES',
    START='START',
    END='END',
    MIN_DURATION='MIN_DURATION',
    FLAGGED = 'FLAGGED',
    STARRED = 'STARRED',
    TIME = 'TIME',
    KEYWORD =  'KEYWORD',
    DISPOSITION = 'DISPOSITION',
    SENTIMENT = 'SENTIMENT',
    PURPOSE = 'PURPOSE',
    CADENCE_STEPS = 'CADENCE_STEPS',
    CADENCES = 'CADENCE',
    INDUSTRIES = 'INDUSTRY',
    TITLES = 'TITLE',
    SENIORITIES = 'SENIORITY',
    CNF_PASS_THROUGH = 'CNF_PASS_THROUGH',
    SESSION_METRIC_PASS_THROUGH = 'SESSION_METRIC_PASS_THROUGH'
}

export const CALL_OUTCOME_OPTIONS: Option[] = [Option.DISPOSITION, Option.SENTIMENT, Option.PURPOSE]
export const PROSPECT_OPTIONS: Option[] = [Option.INDUSTRIES, Option.TITLES, Option.SENIORITIES, Option.CADENCES, Option.CADENCE_STEPS]

export enum CALL_CATEGORY_OPTIONS  {
    ALL_CALLS = 'ALL_CALLS',
    ALL_PROSPECT_CALLS = 'ALL_PROSPECT_CALLS'
}

const OptionToFilterId: {[k in Option]: string} = {
    [Option.CALL_CATEGORY]: "Call Info",
    [Option.USER_IDS]: "Rep Info",
    [Option.STAGES]: "Call Content",
    [Option.REMARK]: "Call Content",
    [Option.TEXT]: "Call Content",
    [Option.KEYWORD]: "Call Content",
    [Option.PROMPT_TYPES]: "Call Content",
    [Option.COUNTERPARTS]: "Call Content",
    [Option.SENIORITIES]: "Prospect",
    [Option.PHONE_NUMBERS]: "Prospect",
    [Option.CADENCE_STEPS]: "Prospect",
    [Option.CADENCES]: "Prospect",
    [Option.INDUSTRIES]: "Prospect",
    [Option.TITLES]: "Prospect",
    [Option.START]: "Call Info",
    [Option.END]: "Call Info",
    [Option.MIN_DURATION]: "Call Info",
    [Option.FLAGGED]: "Call Info",
    [Option.STARRED]: "Call Info",
    [Option.TIME]: "Call Time",
    [Option.DISPOSITION]: "Call Content",
    [Option.SENTIMENT]: "Call Content",
    [Option.PURPOSE]: "Call Content",
    [Option.CNF_PASS_THROUGH]: "Call Content",
    [Option.SESSION_METRIC_PASS_THROUGH]: "Call Content"
}

export const OptionToMultipleFilters: {[k in Option]: boolean} = {
    [Option.CALL_CATEGORY]: false,
    [Option.USER_IDS]: false, // false here implies we can have a single _userPickerRef
    [Option.STAGES]: false,
    [Option.REMARK]: true,
    [Option.TEXT]: true,
    [Option.MIN_DURATION]: false,
    [Option.PROMPT_TYPES]: false,
    [Option.COUNTERPARTS]: true,
    [Option.START]: false,
    [Option.END]: false,
    [Option.PHONE_NUMBERS]: false,
    [Option.FLAGGED]: false,
    [Option.STARRED]: false,
    [Option.TIME]: false,
    [Option.KEYWORD]: true,
    [Option.CADENCE_STEPS]: false,
    [Option.CADENCES]: false,
    [Option.INDUSTRIES]: false,
    [Option.TITLES]: false,
    [Option.SENIORITIES]: false,
    [Option.DISPOSITION]: false,
    [Option.SENTIMENT]: false,
    [Option.PURPOSE]: false,
    [Option.CNF_PASS_THROUGH]: false,
    [Option.SESSION_METRIC_PASS_THROUGH]: false
}

export const OptionToIsTextFilter: {[k in Option]: boolean} = {
    [Option.CALL_CATEGORY]: false,
    [Option.USER_IDS]: false,
    [Option.STAGES]: false,
    [Option.REMARK]: false,
    [Option.TEXT]: true,
    [Option.MIN_DURATION]: false,
    [Option.PROMPT_TYPES]: false,
    [Option.COUNTERPARTS]: false,
    [Option.START]: false,
    [Option.END]: false,
    [Option.PHONE_NUMBERS]: false,
    [Option.FLAGGED]: false,
    [Option.STARRED]: false,
    [Option.TIME]: false,
    [Option.KEYWORD]: false,
    [Option.CADENCE_STEPS]: false,
    [Option.CADENCES]: false,
    [Option.INDUSTRIES]: false,
    [Option.TITLES]: false,
    [Option.SENIORITIES]: false,
    [Option.DISPOSITION]: false,
    [Option.SENTIMENT]: false,
    [Option.PURPOSE]: false,
    [Option.CNF_PASS_THROUGH]: false,
    [Option.SESSION_METRIC_PASS_THROUGH]: false
}

export const OptionToIsDurationFilter: {[k in Option]: boolean} = {
    [Option.CALL_CATEGORY]: false,
    [Option.USER_IDS]: false,
    [Option.STAGES]: false,
    [Option.REMARK]: false,
    [Option.TEXT]: false,
    [Option.MIN_DURATION]: true,
    [Option.PROMPT_TYPES]: false,
    [Option.COUNTERPARTS]: false,
    [Option.START]: false,
    [Option.END]: false,
    [Option.PHONE_NUMBERS]: false,
    [Option.FLAGGED]: false,
    [Option.STARRED]: false,
    [Option.TIME]: true,
    [Option.KEYWORD]: false,
    [Option.CADENCE_STEPS]: false,
    [Option.CADENCES]: false,
    [Option.INDUSTRIES]: false,
    [Option.TITLES]: false,
    [Option.SENIORITIES]: false,
    [Option.DISPOSITION]: false,
    [Option.SENTIMENT]: false,
    [Option.PURPOSE]: false,
    [Option.CNF_PASS_THROUGH]: false,
    [Option.SESSION_METRIC_PASS_THROUGH]: false
}

export const OptionToHumanReadable: {[k in Option]: string} = {
    [Option.CALL_CATEGORY]: "Call Category",
    [Option.USER_IDS]: "Users",
    [Option.STAGES]: "End Stage",
    [Option.REMARK]: "Event",
    [Option.TEXT]: "Phrase",
    [Option.MIN_DURATION]: "Min Duration (min.)",
    [Option.PROMPT_TYPES]: "Prompt Types",
    [Option.COUNTERPARTS]: "Counterparts",
    [Option.START]: "Start",
    [Option.END]: "End",
    [Option.PHONE_NUMBERS]: "Numbers",
    [Option.FLAGGED]: "Has Flag",
    [Option.STARRED]: "Has Star",
    [Option.TIME]: "Occurs",
    [Option.KEYWORD]: "Keyword",
    [Option.CADENCE_STEPS]: "Cadence Steps",
    [Option.CADENCES]: "Cadences",
    [Option.INDUSTRIES]: "Industries",
    [Option.TITLES]: "Titles",
    [Option.SENIORITIES]: "Seniorities",
    [Option.DISPOSITION]: "Disposition",
    [Option.SENTIMENT]: "Sentiment",
    [Option.PURPOSE]: "Purpose",
    [Option.CNF_PASS_THROUGH]: "-",
    [Option.SESSION_METRIC_PASS_THROUGH]: "-"
}

export enum REMARK_FIRST_ORDER_OPTION {
    HAS = 'Has event',
    DOES_NOT_HAVE = 'Does not have event'
} 

export enum TEXT_FIRST_ORDER_OPTION {
    CALL_HAS = 'Call has',
    REP_SAYS = 'Rep says',
    PROSPECT_SAYS = 'Prospect says'
}

const handSvg = <svg width="" height="" viewBox="0 0 92 128" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.29979 62.0262L4.41451 61.534L4.66496 35.9803C4.70305 34.694 5.24595 33.4743 6.17624 32.5852C7.10654 31.696 8.34946 31.2087 9.63619 31.2288H9.66792C10.2986 31.2271 10.9232 31.3508 11.5056 31.5928C12.088 31.8347 12.6165 32.19 13.0603 32.638C13.7102 33.2998 14.1497 34.1389 14.3238 35.0499C14.3313 35.1174 14.4211 35.7761 14.4185 36.0472L14.194 64.4134C15.4721 63.3803 16.8156 62.4308 18.216 61.5709L18.4558 18.0843C18.4943 16.798 19.0376 15.5786 19.9682 14.6898C20.8988 13.801 22.1418 13.3142 23.4285 13.3348H23.4602C24.3785 13.3401 28.9605 13.5964 28.9601 18.125L28.5773 57.1839C29.8978 56.8479 31.2355 56.5837 32.5846 56.3924L33.0516 8.74931C33.0902 7.4632 33.6335 6.24396 34.564 5.35526C35.4945 4.46655 36.7373 3.9798 38.0239 4.00027H38.0561C38.7157 3.99967 39.3688 4.13025 39.9774 4.38441C40.5861 4.63858 41.1381 5.01125 41.6013 5.48074C42.0348 5.9159 42.3773 6.4329 42.6092 7.00165C42.841 7.57041 42.9574 8.17958 42.9517 8.79374L42.4862 56.2746C43.8286 56.4336 45.1615 56.6647 46.4791 56.9667L46.8114 23.0651C46.8365 20.6263 49.5971 18.3377 52.5036 18.3377H52.5382C53.1974 18.337 53.8502 18.4675 54.4586 18.7214C55.067 18.9753 55.6188 19.3476 56.082 19.8167C56.5154 20.2523 56.858 20.7697 57.0897 21.3389C57.3214 21.9081 57.4377 22.5176 57.4318 23.1321L57.0602 61.1507C58.4391 61.9531 59.7662 62.8414 61.0338 63.8101L61.4311 23.1717C61.4437 22.0304 61.2293 20.8981 60.8004 19.8405C60.3715 18.7828 59.7365 17.821 58.9325 17.011C58.1011 16.1654 57.1098 15.4936 56.0163 15.0346C54.9228 14.5757 53.749 14.3388 52.5631 14.3377C50.5357 14.3433 48.5564 14.9553 46.8797 16.0951L46.9509 8.83279C46.9631 7.69202 46.7485 6.56019 46.3195 5.50309C45.8905 4.44598 45.2556 3.48469 44.4519 2.67508C43.6203 1.82925 42.6289 1.15719 41.5353 0.697995C40.4416 0.238798 39.2676 0.00162565 38.0815 0.000273069H38.0234C35.6835 -0.0180412 33.4305 0.88552 31.7516 2.51551C30.0728 4.1455 29.1031 6.37088 29.0524 8.71027L29.0315 10.8405C27.3584 9.83232 25.4373 9.31087 23.4841 9.33474H23.4275C21.0874 9.31651 18.8342 10.2203 17.1553 11.8506C15.4765 13.4809 14.5069 15.7066 14.4565 18.0462L14.3591 28.5904C12.9626 27.7069 11.3453 27.235 9.69273 27.2288H9.6361C7.29591 27.2106 5.04267 28.1146 3.36388 29.7451C1.6851 31.3756 0.715772 33.6015 0.665625 35.9412L0.420066 61.0535L0.337066 61.4676C0.287266 61.9075 -0.856553 72.3372 1.37447 81.0979L1.8391 82.3483C2.53448 80.1925 3.42231 78.1036 4.49186 76.1068C3.92805 71.4334 3.86366 66.7132 4.29979 62.0262Z" fill="black"/>
<path d="M89.5804 45.1584C88.6906 44.3499 87.6435 43.7336 86.5048 43.3481C85.366 42.9627 84.1599 42.8163 82.962 42.9181C79.2732 43.1647 75.5356 45.3737 72.378 49.2145L61.3438 64.0501C62.3979 64.8737 63.4082 65.7519 64.3705 66.6811L75.5259 51.6794C77.8809 48.818 80.6885 47.0793 83.2285 46.9094C83.8822 46.8421 84.5427 46.9123 85.1677 47.1154C85.7927 47.3184 86.3684 47.6499 86.8577 48.0886C89.5027 50.548 87.3576 56.7707 81.12 64.7331C77.4021 69.3701 74.8119 74.8073 73.5535 80.616C74.5895 83.3654 75.3005 86.2265 75.672 89.141C76.1139 87.734 76.4746 86.3027 76.7522 84.8542C77.9942 78.1657 79.6218 73.1335 84.2683 67.1999C92.2679 56.988 94.1049 49.3664 89.5804 45.1584Z" fill="black"/>
<path d="M37.9986 60C31.2738 59.9987 24.6996 61.9917 19.1075 65.7269C13.5154 69.4621 9.1565 74.7717 6.58214 80.9843C4.00778 87.1969 3.33355 94.0334 4.64472 100.629C5.9559 107.225 9.19358 113.284 13.9483 118.039C18.703 122.795 24.7612 126.034 31.3568 127.346C37.9523 128.659 44.7889 127.986 51.002 125.413C57.215 122.839 62.5255 118.481 66.2617 112.89C69.998 107.299 71.9922 100.725 71.9922 94C71.9926 89.5355 71.1137 85.1146 69.4056 80.9897C67.6975 76.8649 65.1936 73.1169 62.037 69.9597C58.8804 66.8025 55.1329 64.298 51.0084 62.5891C46.8839 60.8802 42.4631 60.0004 37.9986 60ZM37.9986 124C32.5308 124.003 27.1665 122.51 22.486 119.683C17.8056 116.856 13.9873 112.804 11.4442 107.963C8.90118 103.123 7.73029 97.6792 8.05823 92.2213C8.38618 86.7634 10.2005 81.4991 13.3048 76.998L60.2107 114.139C57.4041 117.245 53.977 119.727 50.1509 121.426C46.3248 123.125 42.1848 124.002 37.9986 124ZM62.6924 111.002L15.7865 73.8612C20.893 68.2199 27.9535 64.7314 35.5367 64.1029C43.1199 63.4744 50.6584 65.7529 56.6239 70.4765C62.5894 75.2001 66.5356 82.0153 67.6625 89.5406C68.7894 97.0659 67.0126 104.738 62.6924 111.002Z" fill="black"/>
</svg>

const partySvg = <svg viewBox="0 0 68 63" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M67.7744 38.1375C67.4744 37.6375 66.8744 37.5375 66.3744 37.7375C64.2744 38.9375 62.6744 37.6375 59.8744 35.2375C57.8744 33.5375 55.5744 31.5375 52.6744 30.9375C49.5744 30.3375 46.8744 31.7375 45.1744 33.6375C44.6744 33.5375 44.1744 33.5375 43.6744 33.5375C43.6744 33.5375 43.6744 33.5375 43.5744 33.5375C42.3744 33.5375 41.0744 33.7375 39.8744 34.1375C38.9744 32.3375 37.6744 30.5375 36.0744 28.8375C37.9744 27.9375 39.2744 25.9375 39.2744 23.7375C39.2744 20.6375 36.7744 18.0375 33.5744 18.0375C30.8744 18.0375 28.5744 19.9375 28.0744 22.4375C27.0744 21.9375 26.0744 21.5375 25.1744 21.2375C25.5744 20.6375 26.0744 19.9375 26.6744 19.2375C28.1744 17.6375 28.6744 15.7375 28.0744 13.9375C27.9744 13.5375 27.7744 13.1375 27.4744 12.7375C28.4744 10.5375 28.2744 8.1375 27.5744 6.4375C28.4744 5.2375 29.2744 3.8375 29.6744 2.1375C29.7744 1.6375 29.4744 1.0375 28.9744 0.9375C28.4744 0.8375 27.8744 1.1375 27.7744 1.6375C27.4744 2.7375 27.0744 3.7375 26.4744 4.5375C25.7744 3.6375 24.7744 3.0375 23.8744 2.9375C20.7744 2.6375 19.2744 3.9375 18.8744 5.3375C18.4744 6.6375 19.0744 8.0375 20.2744 8.7375C21.4744 9.4375 22.9744 9.4375 24.4744 8.8375C24.9744 8.6375 25.4744 8.3375 25.9744 7.9375C26.1744 8.8375 26.2744 9.9375 25.9744 11.0375C25.3744 10.6375 24.6744 10.3375 23.9744 10.1375C22.3744 9.8375 20.8744 10.2375 19.8744 11.4375C19.1744 12.2375 18.9744 13.2375 19.2744 14.2375C19.6744 15.3375 20.6744 16.2375 21.8744 16.4375C22.7744 16.6375 24.4744 16.5375 26.1744 14.6375C26.4744 15.7375 26.0744 16.8375 25.1744 17.8375C24.2744 18.8375 23.5744 19.7375 23.0744 20.6375C22.2744 20.5375 21.5744 20.4375 20.8744 20.4375C18.7744 20.4375 16.9744 21.1375 15.6744 22.3375C15.0744 22.9375 14.5744 23.7375 14.2744 24.6375L0.174433 59.2375C-0.125567 59.9375 -0.0255668 60.7375 0.374433 61.4375C0.774433 62.1375 1.57443 62.4375 2.27443 62.4375C2.57443 62.4375 2.87443 62.3375 3.17443 62.2375L24.5744 53.4375L37.6744 48.0375C38.5744 47.7375 39.3744 47.2375 39.9744 46.6375C42.3744 44.2375 42.4744 40.2375 40.6744 35.9375C41.5744 35.6375 42.5744 35.5375 43.4744 35.5375C43.5744 35.5375 43.6744 35.5375 43.7744 35.5375C42.3744 38.2375 42.6744 41.1375 44.3744 43.1375C45.3744 44.2375 46.5744 44.6375 47.6744 44.6375C48.6744 44.6375 49.6744 44.2375 50.3744 43.6375C51.9744 42.2375 52.7744 39.5375 50.5744 36.7375C49.6744 35.6375 48.5744 34.7375 47.2744 34.2375C48.5744 33.1375 50.2744 32.5375 52.1744 32.9375C54.5744 33.4375 56.5744 35.1375 58.4744 36.7375C61.1744 39.0375 63.8744 41.3375 67.2744 39.4375C67.8744 39.2375 68.0744 38.6375 67.7744 38.1375ZM23.7744 7.0375C23.1744 7.3375 22.2744 7.5375 21.3744 7.0375C20.9744 6.8375 20.7744 6.3375 20.8744 5.9375C21.0744 5.4375 21.6744 4.9375 23.1744 4.9375C23.3744 4.9375 23.5744 4.9375 23.7744 4.9375C24.1744 4.9375 24.7744 5.3375 25.2744 6.0375C24.7744 6.5375 24.2744 6.8375 23.7744 7.0375ZM24.9744 13.3375C24.1744 14.3375 23.2744 14.8375 22.4744 14.6375C21.9744 14.5375 21.5744 14.2375 21.3744 13.7375C21.2744 13.4375 21.3744 13.1375 21.5744 12.9375C22.0744 12.3375 22.6744 12.2375 23.1744 12.2375C23.3744 12.2375 23.5744 12.2375 23.7744 12.3375C24.2744 12.4375 24.8744 12.7375 25.2744 13.1375C25.0744 13.1375 24.9744 13.2375 24.9744 13.3375ZM33.6744 20.0375C35.6744 20.0375 37.3744 21.6375 37.3744 23.7375C37.3744 25.5375 35.9744 27.1375 34.1744 27.3375C33.9744 27.3375 33.8744 27.4375 33.6744 27.4375C33.4744 27.4375 33.1744 27.4375 32.9744 27.3375C32.8744 27.3375 32.8744 27.3375 32.7744 27.3375C32.5744 27.3375 32.4744 27.2375 32.2744 27.1375C32.1744 27.1375 32.1744 27.0375 32.0744 27.0375C31.9744 26.9375 31.7744 26.9375 31.6744 26.8375C31.5744 26.7375 31.4744 26.7375 31.4744 26.6375C31.3744 26.5375 31.2744 26.5375 31.1744 26.4375C31.0744 26.3375 30.9744 26.2375 30.8744 26.1375C30.8744 26.0375 30.7744 26.0375 30.7744 25.9375C30.6744 25.7375 30.5744 25.6375 30.4744 25.4375C30.2744 25.0375 30.1744 24.6375 30.0744 24.1375C30.0744 24.0375 30.0744 23.9375 30.0744 23.8375C29.9744 21.7375 31.6744 20.0375 33.6744 20.0375ZM22.1744 42.6375C22.2744 42.7375 22.3744 42.8375 22.4744 42.8375C22.8744 43.1375 23.2744 43.5375 23.6744 43.8375C23.7744 43.9375 23.7744 43.9375 23.8744 44.0375C24.2744 44.3375 24.5744 44.5375 24.9744 44.8375C25.0744 44.9375 25.1744 44.9375 25.1744 45.0375C25.5744 45.3375 25.9744 45.5375 26.3744 45.8375C26.4744 45.9375 26.5744 45.9375 26.6744 46.0375C27.0744 46.2375 27.4744 46.5375 27.8744 46.7375H27.9744C28.3744 46.9375 28.7744 47.1375 29.2744 47.3375C29.2744 47.3375 29.3744 47.3375 29.3744 47.4375C20.5744 46.4375 13.7744 41.6375 11.7744 36.6375L12.7744 34.1375C14.0744 36.0375 15.9744 37.5375 18.3744 38.7375C19.1744 39.7375 19.9744 40.7375 20.9744 41.7375C21.3744 41.9375 21.7744 42.3375 22.1744 42.6375ZM24.1744 51.5375C20.0744 51.0375 10.9744 49.2375 8.87443 43.4375L10.6744 38.9375C14.0744 44.8375 21.8744 48.4375 29.5744 49.3375L24.1744 51.5375ZM16.6744 54.6375C12.9744 54.3375 8.37443 52.9375 6.77443 48.5375L7.87443 45.9375C10.4744 50.1375 15.9744 52.0375 20.6744 53.0375L16.6744 54.6375ZM9.97443 57.3375C6.87443 57.3375 4.67443 56.0375 4.27443 54.6375L5.77443 51.0375C7.27443 53.5375 9.77443 55.1375 13.1744 56.0375L9.97443 57.3375ZM15.7744 34.5375C14.8744 33.7375 14.2744 32.8375 13.6744 31.7375L14.2744 30.3375C14.5744 31.7375 15.0744 33.1375 15.7744 34.5375ZM2.47443 60.4375C2.27443 60.5375 2.17443 60.4375 2.07443 60.3375C1.97443 60.2375 2.07443 60.0375 2.07443 60.0375L3.37443 56.9375C4.17443 57.7375 5.17443 58.4375 6.47443 58.8375L2.47443 60.4375ZM38.6744 45.2375C38.2744 45.6375 37.6744 46.0375 37.0744 46.2375C36.4744 46.4375 35.6744 46.5375 34.8744 46.5375C31.5744 46.5375 27.4744 44.6375 23.8744 41.5375C23.7744 41.4375 23.5744 41.3375 23.4744 41.1375C23.0744 40.8375 22.7744 40.4375 22.3744 40.1375C22.0744 39.8375 21.6744 39.4375 21.3744 39.1375C21.2744 39.0375 21.1744 38.8375 21.0744 38.7375C20.8744 38.5375 20.6744 38.3375 20.4744 38.0375C20.3744 37.8375 20.2744 37.7375 20.0744 37.5375C19.8744 37.3375 19.7744 37.1375 19.5744 36.9375C19.4744 36.7375 19.3744 36.6375 19.2744 36.4375C19.1744 36.2375 18.9744 36.0375 18.8744 35.8375C18.7744 35.6375 18.6744 35.5375 18.5744 35.3375C18.3744 35.1375 18.2744 34.8375 18.1744 34.6375C18.0744 34.5375 18.0744 34.4375 17.9744 34.3375C17.7744 33.9375 17.5744 33.6375 17.3744 33.2375C17.3744 33.1375 17.2744 33.0375 17.2744 33.0375C17.1744 32.7375 16.9744 32.4375 16.8744 32.1375C16.8744 32.0375 16.7744 31.9375 16.7744 31.8375C16.6744 31.5375 16.5744 31.3375 16.4744 31.0375C16.4744 30.9375 16.3744 30.8375 16.3744 30.7375C16.2744 30.4375 16.1744 30.1375 16.1744 29.9375C16.1744 29.8375 16.1744 29.8375 16.0744 29.7375C15.9744 29.4375 15.9744 29.1375 15.8744 28.8375V28.7375C15.7744 28.0375 15.7744 27.4375 15.8744 26.8375C15.8744 26.7375 15.8744 26.7375 15.8744 26.7375C15.8744 26.5375 15.9744 26.2375 15.9744 26.0375C15.9744 25.9375 16.0744 25.8375 16.0744 25.7375C16.2744 24.9375 16.6744 24.3375 17.1744 23.8375C18.0744 22.9375 19.2744 22.5375 20.8744 22.5375C21.2744 22.5375 21.6744 22.5375 22.0744 22.6375C21.1744 24.6375 21.0744 26.0375 21.0744 26.4375C21.0744 27.0375 21.4744 27.4375 22.0744 27.4375C22.5744 27.4375 23.0744 27.0375 23.0744 26.4375C23.0744 26.2375 23.1744 24.8375 24.0744 22.9375C25.3744 23.3375 26.6744 23.8375 27.9744 24.6375C28.1744 25.6375 28.5744 26.5375 29.1744 27.3375C29.1744 27.3375 29.1744 27.3375 29.2744 27.4375C29.4744 27.6375 29.5744 27.8375 29.7744 27.9375C29.8744 27.9375 29.8744 28.0375 29.9744 28.0375C30.1744 28.2375 30.3744 28.3375 30.5744 28.4375C30.6744 28.4375 30.7744 28.5375 30.7744 28.5375C30.9744 28.6375 31.1744 28.7375 31.4744 28.8375C31.5744 28.8375 31.6744 28.9375 31.6744 28.9375C31.9744 29.0375 32.1744 29.1375 32.4744 29.1375C32.5744 29.1375 32.5744 29.1375 32.6744 29.1375C32.9744 29.1375 33.2744 29.2375 33.5744 29.2375C33.6744 29.2375 33.7744 29.2375 33.8744 29.2375C35.5744 30.9375 36.8744 32.8375 37.8744 34.7375C35.3744 35.8375 33.0744 37.6375 31.2744 40.0375C30.9744 40.4375 30.9744 41.1375 31.4744 41.4375C31.8744 41.7375 32.5744 41.7375 32.8744 41.2375C34.5744 39.1375 36.5744 37.5375 38.7744 36.5375C40.3744 40.2375 40.4744 43.5375 38.6744 45.2375ZM49.1744 38.0375C50.6744 39.9375 50.1744 41.5375 49.2744 42.2375C48.4744 42.9375 47.0744 43.1375 45.9744 41.8375C44.4744 40.1375 44.8744 37.8375 45.7744 36.2375C45.8744 36.1375 45.8744 36.0375 45.9744 35.9375C47.2744 36.3375 48.3744 37.0375 49.1744 38.0375ZM58.5744 15.2375C60.4744 15.2375 62.0744 13.6375 62.0744 11.7375C62.0744 9.8375 60.4744 8.2375 58.5744 8.2375C56.6744 8.2375 55.0744 9.8375 55.0744 11.7375C55.0744 13.6375 56.5744 15.2375 58.5744 15.2375ZM58.5744 10.2375C59.3744 10.2375 60.0744 10.9375 60.0744 11.7375C60.0744 12.5375 59.3744 13.2375 58.5744 13.2375C57.7744 13.2375 57.0744 12.5375 57.0744 11.7375C57.0744 10.9375 57.6744 10.2375 58.5744 10.2375ZM33.9744 33.1375C33.9744 31.6375 32.7744 30.4375 31.2744 30.4375C29.7744 30.4375 28.5744 31.6375 28.5744 33.1375C28.5744 34.6375 29.7744 35.8375 31.2744 35.8375C32.7744 35.8375 33.9744 34.5375 33.9744 33.1375ZM30.5744 33.1375C30.5744 32.7375 30.8744 32.4375 31.2744 32.4375C31.6744 32.4375 31.9744 32.7375 31.9744 33.1375C31.9744 33.5375 31.6744 33.8375 31.2744 33.8375C30.8744 33.8375 30.5744 33.4375 30.5744 33.1375ZM36.7744 7.9375L40.1744 9.7375L40.5744 13.5375C40.5744 13.9375 40.8744 14.2375 41.1744 14.3375C41.2744 14.3375 41.3744 14.4375 41.4744 14.4375C41.7744 14.4375 41.9744 14.3375 42.1744 14.1375L44.9744 11.5375L48.7744 12.3375C49.1744 12.4375 49.5744 12.2375 49.7744 11.9375C49.9744 11.6375 50.0744 11.2375 49.8744 10.9375L48.2744 7.4375L50.1744 4.1375C50.3744 3.8375 50.3744 3.4375 50.1744 3.0375C49.9744 2.7375 49.5744 2.5375 49.1744 2.6375L45.3744 3.2375L42.7744 0.3375C42.4744 0.0375001 42.1744 -0.0625 41.7744 0.0375C41.3744 0.1375 41.1744 0.4375 41.0744 0.8375L40.3744 4.6375L36.8744 6.2375C36.5744 6.4375 36.2744 6.7375 36.2744 7.1375C36.2744 7.5375 36.4744 7.7375 36.7744 7.9375ZM41.6744 6.2375C41.9744 6.1375 42.1744 5.8375 42.2744 5.5375L42.6744 3.2375L44.2744 4.9375C44.4744 5.1375 44.7744 5.3375 45.1744 5.2375L47.4744 4.9375L46.2744 6.9375C46.0744 7.2375 46.0744 7.5375 46.2744 7.8375L47.2744 9.9375L44.9744 9.5375C44.6744 9.4375 44.2744 9.5375 44.0744 9.8375L42.3744 11.4375L42.1744 9.1375C42.1744 8.8375 41.9744 8.5375 41.6744 8.3375L39.5744 7.2375L41.6744 6.2375ZM46.4744 18.7375C46.0744 18.6375 45.6744 18.7375 45.4744 18.9375C45.1744 19.2375 45.0744 19.6375 45.2744 19.9375L46.0744 22.3375L44.5744 24.3375C44.3744 24.6375 44.2744 25.0375 44.4744 25.4375C44.6744 25.7375 44.9744 25.9375 45.3744 26.0375H47.8744L49.2744 28.1375C49.4744 28.4375 49.7744 28.5375 50.0744 28.5375H50.1744C50.5744 28.4375 50.8744 28.2375 50.9744 27.8375L51.7744 25.4375L54.1744 24.7375C54.5744 24.6375 54.7744 24.3375 54.8744 23.9375C54.9744 23.5375 54.7744 23.1375 54.4744 22.9375L52.4744 21.4375L52.5744 18.9375C52.5744 18.5375 52.3744 18.2375 52.0744 18.0375C51.7744 17.8375 51.3744 17.9375 50.9744 18.1375L48.9744 19.6375L46.4744 18.7375ZM49.5744 21.4375L50.3744 20.8375V21.8375C50.3744 22.1375 50.4744 22.4375 50.7744 22.6375L51.5744 23.2375L50.5744 23.5375C50.2744 23.6375 49.9744 23.8375 49.8744 24.2375L49.5744 25.2375L48.9744 24.4375C48.7744 24.1375 48.4744 24.0375 48.1744 24.0375H47.1744L47.7744 23.2375C47.9744 22.9375 48.0744 22.6375 47.9744 22.3375L47.6744 21.3375L48.6744 21.6375C48.9744 21.7375 49.3744 21.6375 49.5744 21.4375Z" fill="black"/>
</svg>

const flagSvg = 
<svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fillRule="evenodd" clipRule="evenodd" d="M4.23111 31.2496V47.9255C4.23111 49.0713 3.29192 50 2.11537 50C0.946983 50 0 49.0646 0 47.9255V2.07447C0 0.928751 0.946622 0 2.11682 0H48.6546C49.8238 0 50.3284 0.818226 49.7758 1.8384L43.3051 13.7867C42.7551 14.8019 42.7526 16.4433 43.3051 17.4635L49.7758 29.4117C50.3255 30.427 49.8247 31.2501 48.6546 31.2501L4.23111 31.2496ZM4.23111 4.16645V27.0826H43.7198L39.5719 19.4235C38.3561 17.1788 38.3578 14.0671 39.5719 11.8251L43.7198 4.16608L4.23111 4.16645Z" fill="black"/>
</svg>


const starSvg = 
<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.0002 0.833374L12.8327 6.57171L19.1668 7.49754L14.5835 11.9617L15.6652 18.2684L10.0002 15.2892L4.33516 18.2684L5.41683 11.9617L0.833496 7.49754L7.16766 6.57171L10.0002 0.833374Z" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>


enum Presets {
    MEETING_BOOKED = 'MEETING_BOOKED',
    OBJECTIONS = 'OBJECTIONS',
    FLAGGED = 'FLAGGED',
    STARRED = 'STARRED',
}

const PresetToInformation: {[k in Presets]: string} = {
    [Presets.MEETING_BOOKED]: 'Filter for calls with booked meetings',
    [Presets.OBJECTIONS]: 'Fiter for calls with prospect objections',
    [Presets.FLAGGED]: 'Filter for flagged calls',
    [Presets.STARRED]: 'Filter for starred calls'
}

const PresetToSvg: {[k in Presets]: JSX.Element} = {
    [Presets.MEETING_BOOKED]: partySvg,
    [Presets.OBJECTIONS]: handSvg,
    [Presets.FLAGGED]: flagSvg,
    [Presets.STARRED]: starSvg,
}

export enum STAR_FILTER {
    IS_STARRED = 'Is starred',
    NO_FILTER = 'No filter'
}

export enum FLAGGED_FILTER {
    IS_FLAGGED = 'Is flagged',
    IS_REVIEWED = 'Is Reviewed',
    NO_FILTER = 'No Filter'
}

export enum PlayMetadata {
    PARTY_ROLE = 'PARTY_ROLE',
    LOOKBACK_RULE = 'LOOKBACK_RULE',
    LOOKBACK_HORIZON = 'LOOKBACK_HORIZON',
    TIME_TYPE = 'TIME_TYPE',
}

export type PopulationFilter =  {
    uuid?: string,
    option: Option;
    selectedDate?: Date,
    valueFilterOption: FilterOption__STRICT[],
    metadataFilterOption?: FilterOption__STRICT[],
    multiMetadataFilterOption?: PlayMetadataOptions
    groupsFilterOption?: FilterOption__STRICT[],  // for USERS_AND_GROUPS to preserve group ids without string hacking
    selectedUserPickerPresets?: UserPickerSelectionType[],
    notRemovable?: boolean
}

function _getOptionForOutcome(field_name: string) {
    if (field_name.includes('disposition')) return Option.DISPOSITION
    if (field_name.includes('sentiment')) return Option.SENTIMENT
    if (field_name.includes('purpose')) return Option.PURPOSE
    return null
}


export function getDefaultFilterOption(
    option: Option,
    populationFilters?: PopulationFilter[],
    dispositionOptions?: ProspectInfoChoices | null,
    prospectInfoOptions?: ProspectInfoChoices | null,
    allRemarksAllowed?: boolean,
): FilterOption__STRICT[] {
    if (option === Option.USER_IDS) {
        return []
    } else if (option === Option.STAGES) {
        return Object.values(STAGE).filter((v) => (populationFilters ?? []).find((v) => v.option === Option.CALL_CATEGORY)?.valueFilterOption.find((v) => v.selected)?.value === CALL_CATEGORY_OPTIONS.ALL_PROSPECT_CALLS ? ![STAGE.DIALED].includes(v) : true).map((v: STAGE) => {return {'label': StageToHumanReadable[v], 'value': v, 'selected': false}})
    } else if (option === Option.REMARK) {
        if (!allRemarksAllowed) return [...Array.from(REMARKS_OBJECTIONS), ...Array.from(REMARKS_BEHAVIORS), ...Array.from(REMARKS_OUTCOMES)].filter((value: [Remark, [string, string]]) => REMARKS_ALLOWED.includes(value[0])).map((value: [Remark, [string, string]]) => {return {'label': value[1][0], 'selected': false, 'value': value[0], 'id': value[1][1]}})
        else return [...Object.values(Remark)].map((v) => {return {'label': REMARK_TO_HUMAN_READABLE[v], 'value': v, 'selected': false}})
    } else if (option === Option.CALL_CATEGORY) {
        return [{'label': 'All Calls', 'value': CALL_CATEGORY_OPTIONS.ALL_CALLS, 'selected': false}, {'label': 'All Prospect Calls', 'value': CALL_CATEGORY_OPTIONS.ALL_PROSPECT_CALLS, 'selected': true}]
    } else if (option === Option.COUNTERPARTS) {
        return Object.values(Counterpart).map((v) => {return {'label': COUNTERPART_TO_HUMAN_READABLE[v], 'value': v, 'selected': false}})
    }else if (option === Option.PROMPT_TYPES) {
        return Object.values(PromptType).filter((v) => PromptTypeToHumanReadable.has(v)).map((v) => {return {'label': PromptTypeToHumanReadable.get(v) ?? '', 'value': v, 'selected': false}})
    } else if (OptionToIsTextFilter[option]) {
        return [{'label': '', 'value': '', 'selected': true}]
    } else if (option === Option.MIN_DURATION) {
        return [{'label': 'Duration', 'value': "0", 'selected': true }]
    } else if (option === Option.FLAGGED) {
        return Object.values(FLAGGED_FILTER).map((v) => {return {'label': v, 'value': v, 'selected': v === FLAGGED_FILTER.NO_FILTER}})
    } else if (option === Option.STARRED) {
        return Object.values(STAR_FILTER).map((v) => {return {'label': v, 'value': v, 'selected': v === STAR_FILTER.IS_STARRED}})
    } else if (CALL_OUTCOME_OPTIONS.includes(option) && dispositionOptions && dispositionOptions.find((v) => _getOptionForOutcome(v.field_name) === option)) {
        const matching_option = dispositionOptions.find((v) => _getOptionForOutcome(v.field_name) === option)?.choices ?? []
        return matching_option.map((v) => {return {'label': v, 'value': v, 'selected': false}})
    } else if (PROSPECT_OPTIONS.includes(option) && prospectInfoOptions && prospectInfoOptions.find((v) => v.field_name.toLowerCase() === option.toLowerCase())){
        const matching_option = prospectInfoOptions.find((v) => v.field_name.toLowerCase() === option.toLowerCase())?.choices ?? []
        return matching_option.map((v) => {return {'label': v, 'value': v, 'selected': false}})
    }
    return []
}

export function getDefaultMetadataFilterOption(option: Option): FilterOption__STRICT[] | undefined {
    if (option === Option.REMARK) {
        return Object.values(REMARK_FIRST_ORDER_OPTION).map((v, idx) => {return {'label': v, 'value': v, 'selected': idx == 0}})
    } else if (option === Option.TEXT) {
        return Object.values(TEXT_FIRST_ORDER_OPTION).map((v, idx) => {return {'label': v, 'value': v, 'selected': idx == 0}})
    }
    return undefined
}

export function getNameForLookbackRule(x: Option, lookbackRule?: LookbackRule): string {
    if (x === Option.TEXT) {
        switch (lookbackRule) {
            case undefined:
                return 'Is saying'
            case LookbackRule.PRECEDED_BY:
                return 'Previously said'
            case LookbackRule.NOT_PRECEDED_BY:
                return 'Has not previously said'
        }
    } else if (x === Option.REMARK) {
        switch (lookbackRule) {
            case undefined: 
                return 'Is currently'
            case LookbackRule.PRECEDED_BY:
                return 'Has already'
            case LookbackRule.NOT_PRECEDED_BY:
                return 'Has not already'
        }
    }
    return ''
}

export function getNameForProspect(partyRole?: PartyRole) {
    switch (partyRole) {
        case undefined: 
            return 'Anyone'
        case PartyRole.PROSPECT:
            return 'Prospect'
        case PartyRole.REP:
            return 'Rep'
    }
}

export type PlayMetadataOptions =  {[k in PlayMetadata]: FilterOption__STRICT[] | number | undefined}  

export function getDefaultMultiMetadataFilterOption(
    option: Option, lookbackRule?: LookbackRule, 
    partyRole?: PartyRole, horizon?: number,
    timeFilterType?: TimeFilterType): PlayMetadataOptions | undefined {
    const lookbackRuleDefault: FilterOption__STRICT[]  = [...Object.values(LookbackRule), NO_LOOKBACK_RULE].map((v) => {return {'label': getNameForLookbackRule(option, v === NO_LOOKBACK_RULE ? undefined : v as LookbackRule), 'value': v, 'selected': lookbackRule ? lookbackRule === v : v === NO_LOOKBACK_RULE}})
    const defaultPartyRole: FilterOption__STRICT[] = [...Object.values(PartyRole), NO_PARTY_ROLE].map((v) => {return {'label': getNameForProspect(v === NO_PARTY_ROLE ? undefined : v as PartyRole), 'value': v, 'selected': partyRole ? partyRole === v : v === NO_PARTY_ROLE}})
    
    if ([Option.TEXT, Option.REMARK].includes(option)) {
        // lookback horizon
        // lookback rule 
        return {
            [PlayMetadata.LOOKBACK_RULE]: lookbackRuleDefault,
            [PlayMetadata.PARTY_ROLE]: [Option.TEXT].includes(option) ? defaultPartyRole : undefined,
            [PlayMetadata.LOOKBACK_HORIZON]: horizon,
            [PlayMetadata.TIME_TYPE]: undefined,
        }
    } else if ([Option.TIME].includes(option)) {
        return {
            [PlayMetadata.LOOKBACK_RULE]: undefined,
            [PlayMetadata.PARTY_ROLE]: undefined,
            [PlayMetadata.LOOKBACK_HORIZON]: undefined,
            [PlayMetadata.TIME_TYPE]: [...Object.values(TimeFilterType)].map((v) => {return {'label': v, 'selected': v === timeFilterType, 'value': v}}),
        }
    } 
    return undefined
}

export enum ViewType {
    SIMPLE_IN_LINE = 'SIMPLE_IN_LINE',
    TRIGGERS = 'TRIGGERS',
    DIAGNOSTICS = 'DIAGNOSTICS',
    RESEARCH = 'RESEARCH',
    SIMPLE_ADD_BUTTON = 'SIMPLE_ADD_BUTTON',
}

type FilterSelectorProps = {
    populationFilters: PopulationFilter[]
    updatePopulationFilters: (filters: PopulationFilter[]) => void,
    
    canShowOption: Option[]
    hotSelectView?: boolean
    diagnosticsView?: boolean
    isCoachingView?: boolean
    viewType?: ViewType

    noTextDelay?: boolean
    startWithExpandedFilter?: boolean
    updateConditionName?: (name: string) => void
    conditionName?: string

    requiresMatchesRequested?: boolean
    matchRequested?: boolean
    showMatches?: boolean
    numberMatches?: number
    totalPossibleMatches?: number
    onMatchClick?: () => void

    canShowLanguageOption?: boolean
    languageOption?: FilterOption[]
    updateLanguageOption?: (filter: FilterOption[]) => void,

    stemmingOption?: boolean
    updateStemmingOption?: (updated: boolean) => void,

    noOpenLastFilter?: boolean

    // props filled in by redux
    seesMultipleUsers: boolean | null
    hasUnassignedUsers: boolean
    dispatch: Dispatch<AnyAction>

    customHeaderElementFn?: (populationFilter: PopulationFilter) => JSX.Element
    cannotRemoveFilter?: boolean

    userPickerProps?: Pick<
        UserPickerImpl extends React.Component<infer Props> ? Props : never,
        "allUsersLabel" | "allSubteamsLabel" | "prefilterUsers" | "allSubteamsExcludesUnassigned" | "allUsersTogglesAllSubteams" | "showTopLevelTeamGrouping"
    >
    optionNameOverrides?: {[k in Option]?: string}
    defaultToClassic?: boolean

    dispositionOptions: ProspectInfoChoices | null
    prospectInfoOptions: ProspectInfoChoices | null
}

type FilterSelectorState = {
    expandedFilter?: boolean
}

export class FilterSelectorImpl extends React.Component<FilterSelectorProps, FilterSelectorState> {

    _userPickerRef: React.RefObject<UserPickerImpl> = React.createRef()

    constructor(props: FilterSelectorProps) {
        super(props)
        this.state = {
            expandedFilter: this.props.startWithExpandedFilter
        }
    }

    getUserBuckets(): UserPickerBucket[] {
        return this._userPickerRef.current?.getSelectionBuckets() ?? []
    }

    _getVariant(): TypographyVariant {
        return this.props.hotSelectView ? this.props.isCoachingView ? 'mediumParagraph' : 'smallParagraph' : this.props.viewType === ViewType.RESEARCH ? 'smallParagraph' : 'largeParagraph'
    }

    _jsonifyParams(params: any) {
        return Array.from(Object.entries(params)).map(([key, value]) => JSON.stringify(value)).join('')
    }
    
    componentDidUpdate(prevProps: Readonly<FilterSelectorProps>, prevState: Readonly<FilterSelectorState>, snapshot?: any): void {
        // for all text filters..., we need to set timeouts if the id values have changed
        if (!this.props.noTextDelay) {
            const prevTextFilters = prevProps.populationFilters.filter((v) => OptionToIsTextFilter[v.option])
            const currentTextFilters = this.props.populationFilters.filter((v) => OptionToIsTextFilter[v.option])
            for (const currentTextFilter of currentTextFilters) {
                // find the corresponding option and check if the id value has changed
                const correspondingOption = prevTextFilters.find((v) => v.uuid === currentTextFilter.uuid)
                if (correspondingOption) {
                    // check against the ID's and see if they're all the same.... 
                    const mismatchedValues = currentTextFilter.valueFilterOption.filter((v) => correspondingOption.valueFilterOption.find((c) => c.id === v.id)?.label !== v.label)
                    // for all the mistmached values, we have to set a timeout to update the labeled state..... 
                    for (const mismatched of mismatchedValues) {
                        setTimeout(() => {
                            // need to get the corresponding filter option
                            const filterOptionIdx = this.props.populationFilters.findIndex((v) => v.uuid === currentTextFilter.uuid)
                            if (filterOptionIdx === -1) return null
                            const filterOption = this.props.populationFilters[filterOptionIdx]
                            // need to get the corresponding filter value? 
                            const filterValueIdx = filterOption.valueFilterOption.findIndex((v) => v.id === mismatched.id)
                            if (filterValueIdx === -1) return null
                            const filterValue = filterOption.valueFilterOption[filterValueIdx] 
                            if (!filterValue || filterValue.value === mismatched.label || filterValue.label !== mismatched.label) return null
                            // need to check that the label isn't updated and that the id is still the same (basically the person has stopped typing)
                            // pretty painful state update lol
                            this.props.updatePopulationFilters([
                                    ...this.props.populationFilters.slice(0, filterOptionIdx),
                                    {...this.props.populationFilters[filterOptionIdx], 
                                        'valueFilterOption': [
                                            ...this.props.populationFilters[filterOptionIdx].valueFilterOption.slice(0, filterValueIdx),
                                            {...this.props.populationFilters[filterOptionIdx].valueFilterOption[filterValueIdx], 'value': filterValue.label.toString()},
                                            ...this.props.populationFilters[filterOptionIdx].valueFilterOption.slice(filterValueIdx + 1)
                                        ]
                                    },        
                                    ...this.props.populationFilters.slice(filterOptionIdx + 1)])
                        }, 1*SEC_TO_MS)
                    }
                }
            }
        }
    }


    _getAllProspectCalls(): STAGE[] {
        return [STAGE.CONTACT, STAGE.PITCHED, STAGE.CONVERSATION, STAGE.BOOKED]
    }


    _renderModifyButton(onClick?: () => void, isMinus?: boolean, backgroundColor?: string): JSX.Element {
        return <div onClick={onClick?.bind(this)} className={`cursor-pointer w-6 h-6 opacity-75 hover:opacity-100 flex justify-center items-center text-center border border-gray-300 hover:border-gray-400 transition-colors duration-200 ${isMinus ? 'hover:bg-red-50' : 'hover:bg-blue-50'}`} 
        style={{'userSelect': 'none', 'borderRadius': '50%', 'backgroundColor': backgroundColor ?? 'white'}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className={isMinus ? 'text-red-600' : 'text-blue-600'}>
                {isMinus ? 
                    <path d="M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> :
                    <path d="M12 6L12 18M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                }
            </svg>
        </div>
    }

    _getDefaultFilterOption(option: Option): FilterOption__STRICT[] {
        return getDefaultFilterOption(option,
            this.props.populationFilters,
            this.props.dispositionOptions,
            this.props.prospectInfoOptions,
            false,
            )
    }

    _getDefaultMetadataFilterOption(option: Option): FilterOption__STRICT[] | undefined {
        return getDefaultMetadataFilterOption(option)
    }

    _renderSortOrder(matchingString: string): number {
        return ["Rep Info", "Call Content", "Prospect Info", "Call Info", "Call Time"].findIndex((v) => v == matchingString)
    }

    _getOption(field_name: string) {
        if (field_name.includes('disposition')) return Option.DISPOSITION
        if (field_name.includes('sentiment')) return Option.SENTIMENT
        if (field_name.includes('purpose')) return Option.PURPOSE
        return null
    }

    _hasDataForOption(option: Option): boolean {
        if (CALL_OUTCOME_OPTIONS.includes(option)) return this.props.dispositionOptions !== null && (this.props.dispositionOptions.find((v) => this._getOption(v.field_name) === option)?.choices ?? []).length > 0
        if (PROSPECT_OPTIONS.includes(option)) return this.props.prospectInfoOptions !== null && (this.props.prospectInfoOptions.find((v) => v.field_name.toLowerCase() === option.toLowerCase())?.choices ?? []).length > 0
        return true
    }

    _filtersToBeSelected() {
        const filtersIncluded = this.props.populationFilters.map((v) => v.option)
        return Object.values(Option).filter((v: Option) => (
            this.props.canShowOption.includes(v)
            && this._hasDataForOption(v)
            && !(v === Option.USER_IDS && !this.props.seesMultipleUsers)
            && (OptionToMultipleFilters[v] || !filtersIncluded.includes(v))))
    }

    _renderPlusFilter(): JSX.Element {
        return (
        <Filter
        widthFit={true}
        closeOnSelect={true}
        filterType={FilterType.SINGLE} 
        variant={this._getVariant()}
        selectableFilterProps={
            {
                'noMaxHeight': true,
                'headerElement': this._renderModifyButton(),
                'groupById': true,
                'filterOptions': this._filtersToBeSelected().map((v: Option) => {return {'label': OptionToHumanReadable[v], 'selected': false, 'value': v, 'id': OptionToFilterId[v]}}).sort((a, b) => this._renderSortOrder(b.id) - this._renderSortOrder(a.id)),
                'onFilterUpdate': (updatedFilter: FilterOption[]) => {
                    const value = updatedFilter.find((v) => v.selected)
                    if (!value) return
                    this._addPopulationFilter(value.value as Option)
                }
            }
        } />)
    }
   
    _renderFirstSectionFilterElement(idx: number) {
        const populationFilter = this.props.populationFilters[idx]
        const option = populationFilter.option
        return (<div className="py-1.5 flex h-full max-w-full grow focus:outline-none items-center px-[3px]">
        <Typography variant={this._getVariant()} color={'black'}>
          {populationFilter.metadataFilterOption
           ? populationFilter.metadataFilterOption.find((v) => v.selected)?.value
           : (this.props.optionNameOverrides?.[option] ?? OptionToHumanReadable[option])}
        </Typography>
      </div>)
    }

    _renderDurationFilter(populationFilter: PopulationFilter, idx: number) {
        const selected = populationFilter.valueFilterOption.filter((v) => v.selected)
        return <NumericInput min={0} onChange={(value: number | null) => {
            if (this.props.populationFilters[idx].uuid !== populationFilter.uuid) return
            this.props.updatePopulationFilters([
                ...this.props.populationFilters.slice(0, idx),
                {...this.props.populationFilters[idx], 'valueFilterOption': [{'label': '', 'value': value !== null ? value.toString() : "0", 'selected': value !== null}]},
                ...this.props.populationFilters.slice(idx + 1)])
        }} step={1} precision={0} value={selected && selected.length === 1 ? parseFloat(selected[0].value) : 0}/>
    }

    _renderUserPickerHeaderText(populationFilter: PopulationFilter): string {
        const selectedUserItems = populationFilter.valueFilterOption.filter(v => v.selected)
        const selectedGroupItems = (populationFilter.groupsFilterOption ?? []).filter(v => v.selected)
        const selectedAllUsers = (populationFilter.selectedUserPickerPresets ?? []).some(v => v === UserPickerSelectionType.ALL_USERS)
        const selectedAllSubteams = (populationFilter.selectedUserPickerPresets ?? []).some(v => v === UserPickerSelectionType.ALL_SUBTEAMS)
        const selectedLabels = [...selectedGroupItems.map(v => "in " + v.label), ...selectedUserItems.map(v => v.label)]
        return (
            selectedAllUsers || (selectedAllSubteams && (!this.props.hasUnassignedUsers || !this.props.userPickerProps?.allSubteamsExcludesUnassigned)) ? 'All'
            : selectedAllSubteams ? 'All Subteams'
            : selectedLabels.length === 0 ? 'Select'
            : selectedLabels.length === 1 ? selectedLabels[0]
            : selectedLabels.length < (this.props.hotSelectView ? 3 : 4) ? selectedLabels.map(v => `"${v}"`).join(' or ')
            : selectedGroupItems.length === 0 ? `${selectedUserItems.length} users`
            : selectedUserItems.length === 0 ? `${selectedGroupItems.length} groups`
            : `${selectedGroupItems.length} groups + ${selectedUserItems.length} users`
        )
    }

    _renderUserPicker(populationFilter: PopulationFilter, headerElement: React.ReactNode, idx: number) {
        const selectedUserItems = populationFilter.valueFilterOption.filter(v => v.selected)
        const selectedGroupItems = (populationFilter.groupsFilterOption ?? []).filter(v => v.selected)
        const selected: UserPickerSelection[] = [
            ...selectedUserItems.map(v => {return {id: v.value, type: UserPickerSelectionType.USER}}),
            ...selectedGroupItems.map(v => {return {id: v.value, type: UserPickerSelectionType.USER_GROUP}}),
        ]
        if ((populationFilter.selectedUserPickerPresets ?? []).some(v => v === UserPickerSelectionType.ALL_SUBTEAMS)) {
            selected.push({type: UserPickerSelectionType.ALL_SUBTEAMS})
        }
        if ((populationFilter.selectedUserPickerPresets ?? []).some(v => v === UserPickerSelectionType.ALL_USERS)) {
            selected.push({type: UserPickerSelectionType.ALL_USERS})
        }
        return <UserPicker
            {...this.props.userPickerProps}
            ref={this._userPickerRef}
            headerElement={headerElement}
            selected={selected}
            onUpdate={updates => {
                const currentPopulationFilter = this.props.populationFilters[idx]
                if (currentPopulationFilter.uuid !== populationFilter.uuid) return
                const newPopulationFilter: PopulationFilter = {
                    ...currentPopulationFilter,
                    valueFilterOption: this._userPickerRef.current?.getDirectlySelectedUsers().map(u => {return {selected: true, value: u.user_id, label: u.user_name}}) ?? [],
                    groupsFilterOption: this._userPickerRef.current?.getDirectlySelectedGroups().map(g => {return {selected: true, value: g.user_group_id, label: g.user_group_name}}) ?? [],
                    selectedUserPickerPresets: this._userPickerRef.current?.getSelectedPresets(),
                }
                this.props.updatePopulationFilters([
                    ...this.props.populationFilters.slice(0, idx),
                    newPopulationFilter,
                    ...this.props.populationFilters.slice(idx + 1),
                ])
            }}
        />
    }

    _renderSecondSectionFilterElement(idx: number) {
        const populationFilter = this.props.populationFilters[idx]
        const option = this.props.populationFilters[idx].option
        const selected = this.props.populationFilters[idx].valueFilterOption.filter((v) => v.selected)
        const text = (
            option === Option.USER_IDS ? this._renderUserPickerHeaderText(populationFilter)
            : selected.length === 0 ? `Select ${OptionToHumanReadable[option]}`
            : selected.length === 1 ? selected[0].label
            : selected.length < (this.props.hotSelectView ? 3 : 4) ? selected.map((v) => `"${v.label}"`).join(' or ')
            : `${selected.length} selected`
        )
        return (<div className="flex h-full items-center justify-center">
        <div className="flex h-full max-w-full grow focus:outline-none" style={{'cursor': 'pointer'}}>
          <div className="flex h-full flex-nowrap items-center justify-center gap-1">
            <div className="min-h-5 flex select-none items-center justify-center rounded py-1.5 px-1.5 h-fit bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:border-blue-300 transition-colors duration-200">
                {OptionToIsTextFilter[option] ? this._renderTextFilter(idx)
                : [Option.START, Option.END].includes(option) ?
                    <div style={{'maxWidth': '40px'}}>
                        <DatePicker selected={this.props.populationFilters[idx].selectedDate} wrapperClassName='react-datepicker-custom-text-br'
                        dateFormat={"MM/dd"}
                        placeholderText={"Date"}
                        onChange={(date: Date | null) => {
                            if (this.props.populationFilters[idx].uuid !== populationFilter.uuid) return
                            this.props.updatePopulationFilters([
                                ...this.props.populationFilters.slice(0, idx),
                                {...this.props.populationFilters[idx], 'selectedDate': date ?? undefined},
                                ...this.props.populationFilters.slice(idx + 1)
                            ])
                        }} />
                    </div>
                : [Option.MIN_DURATION].includes(option) ? this._renderDurationFilter(populationFilter, idx)
                : <Typography style={{'textAlign': 'left'}} variant={this._getVariant()} color='#1E40AF'>{text}</Typography>}
            </div>
          </div>
        </div>
      </div>)
    }

    _onMetadataFilterUpdate(updatedFilters: FilterOption[], idx: number, populationFilter: PopulationFilter) {
        const selected = updatedFilters.find((v) => v.selected)
        if (this.props.populationFilters[idx].uuid !== populationFilter.uuid) return
            this.props.updatePopulationFilters([
                ...this.props.populationFilters.slice(0, idx), 
                {...this.props.populationFilters[idx], 'metadataFilterOption': this.props.populationFilters[idx].metadataFilterOption?.map((v) => {return {...v, 'selected': selected?.value === v.value}})},
                ...this.props.populationFilters.slice(idx + 1)
            ])
    }

    _onRemoveFilter(idx: number) {
        this.props.updatePopulationFilters([...this.props.populationFilters.slice(0, idx), ...this.props.populationFilters.slice(idx + 1)])
    }

    _renderFilterElement(idx: number, hideRemove: boolean, isLast?: boolean): JSX.Element {
        const populationFilter = this.props.populationFilters[idx]
        return(
        <div 
        style={{'boxShadow': 'rgba(0, 0, 0, 0.35) 0px 5px 15px', 'cursor': [Option.MIN_DURATION, Option.START, Option.END].includes(populationFilter.option) ? 'initial' : 'pointer'}} 
        className="bg-white w-fit flex items-center group relative select-none rounded border-gray-200 dark:border-zinc-700" 
        >
            {populationFilter.metadataFilterOption ? 
            <Filter 
                filterType={FilterType.SINGLE}
                closeOnSelect={true}
                variant={this._getVariant()}
                selectableFilterProps={
                    {
                        'headerElement': this._renderFirstSectionFilterElement(idx),
                        'filterOptions': populationFilter.metadataFilterOption,
                        'onFilterUpdate': (updatedFilters: FilterOption[]) => this._onMetadataFilterUpdate.bind(this)(updatedFilters, idx, populationFilter)
                    }
                }
            /> : this._renderFirstSectionFilterElement(idx)}
            {populationFilter.metadataFilterOption && !OptionToIsTextFilter[populationFilter.option] ? 
                this._renderCustomFilter(idx, isLast) 
            : this._renderSecondSectionFilterElement(idx)}
          {hideRemove || this.props.populationFilters[idx].notRemovable ? null : <button onClick={(e) => {
            e.stopPropagation()
            this._onRemoveFilter(idx)
            }} type="button" className="absolute -top-1.5 -right-1.5 hidden h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-200 bg-gray-100 group-hover:flex dark:border-zinc-700 dark:bg-zinc-800 cursor-pointer">
              <div className="flex max-w-full grow">
                {React.cloneElement(xIcon, {className: "h-full w-full text-gray-500 dark:text-zinc-400"})}
            </div>
        </button>}
      </div>
        )
      }

    _onMainFilterUpdate(updatedFilters: FilterOption[], idx: number) {
        if ((idx + 1) > this.props.populationFilters.length) return null
        // @ts-ignore
        const toUpdate: FilterOption__STRICT[] = this.props.populationFilters[idx].valueFilterOption.map((value: FilterOption) => {
            const filter = updatedFilters.find((v: FilterOption) => v.value == value.value)
            if (filter) {
                return {
                ...value, 'selected': filter.selected
                }
            } else {
                return {
                ...value
                }
            }
            })
        this.props.updatePopulationFilters([...this.props.populationFilters.slice(0, idx), {
                ...this.props.populationFilters[idx], 'valueFilterOption': toUpdate
            }, ...this.props.populationFilters.slice(idx + 1)])
    }

    _renderCustomFilter(idx: number, isLast?: boolean): JSX.Element | null {
        
        if ((idx + 1) > this.props.populationFilters.length) return null
        const option = this.props.populationFilters[idx].option
        const headerElement = this.props.customHeaderElementFn ? this.props.customHeaderElementFn(this.props.populationFilters[idx]) :
        this.props.populationFilters[idx].metadataFilterOption ? this._renderSecondSectionFilterElement(idx) : this._renderFilterElement(idx, this.props.populationFilters[idx].option === Option.CALL_CATEGORY, this.props.populationFilters.length - 1 === idx)

        if (option === Option.USER_IDS && !this.props.defaultToClassic) { return this._renderUserPicker(this.props.populationFilters[idx], headerElement, idx) }
        return <Filter variant={this._getVariant()} filterType={[Option.CALL_CATEGORY, Option.STARRED, Option.FLAGGED].includes(option) ? FilterType.SINGLE : FilterType.MULTI}
        closeOnSelect={[Option.CALL_CATEGORY, Option.FLAGGED, Option.STARRED].includes(option)} 
        selectableFilterProps={{
            'openFilterDefault': !this.props.noOpenLastFilter && isLast,
            'headerElement': headerElement,
            'filterOptions': this.props.populationFilters[idx].valueFilterOption,
            'onFilterUpdate': (updatedFilters: FilterOption[]) => this._onMainFilterUpdate.bind(this)(updatedFilters, idx),
            'groupById': (
                [Option.REMARK, Option.KEYWORD, Option.USER_IDS].includes(this.props.populationFilters[idx].option)
            ),
            }}
        />
    }

    _renderTextFilter(idx: number): JSX.Element | null {
        const populationFilterUUId = this.props.populationFilters[idx].uuid
        return (
        <div className="flex gap-2" style={{
            'boxShadow': this.props.viewType === ViewType.TRIGGERS ? 'rgba(0, 0, 0, 0.1) 0px 3px 3px' : 'initial',
            'borderRadius': this.props.viewType === ViewType.TRIGGERS ? '10px' : '0',
            'padding': this.props.viewType === ViewType.TRIGGERS ? '4px' : '0',
            'width': this.props.viewType === ViewType.TRIGGERS ? 'fit-content' : '100%',
            'flexWrap': 'wrap',
            'flexDirection': this.props.hotSelectView ? 'column' : 'row'}}>
            {this.props.populationFilters[idx].valueFilterOption.map((v, pIdx, arr) => {
                return (
                    <div key={populationFilterUUId} className="flex flex-row gap-1 items-center">
                    <div style={{'maxWidth': '125px', 'width': '125px'}}>
                    <EditableAutosizedInput 
                    minHeight={"20px"}
                    focus={(pIdx === arr.length - 1) && v.label === ''}
                    fontSize={11}
                    blurOnEnter={true}
                    prompt={v.label.toString()} 
                    maxNumber={1}
                    placeholder={'Enter text'}
                    editFunctions={{
                        addEntry: () => {},
                        updateEntry: (_: number, updatedEntry: string, forceRefreshOnEnter?: boolean) => {
                            if (this.props.viewType !== ViewType.TRIGGERS && this.props.populationFilters[idx].uuid !== populationFilterUUId) return null
                            let updatedValueFilterOption: FilterOption__STRICT[] = [...this.props.populationFilters[idx].valueFilterOption.slice(0, pIdx), 
                            {...this.props.populationFilters[idx].valueFilterOption[pIdx], 'label': updatedEntry, 'value': (this.props.noTextDelay || forceRefreshOnEnter) ? updatedEntry : this.props.populationFilters[idx].valueFilterOption[pIdx].value}, 
                            ...this.props.populationFilters[idx].valueFilterOption.slice(pIdx + 1)]
                            if (this.props.viewType === ViewType.TRIGGERS && forceRefreshOnEnter) {
                                // need to add a new label.... 
                                updatedValueFilterOption = [...updatedValueFilterOption.slice(0, pIdx + 1), {'label': '', 'value': '', 'selected': true} ,...updatedValueFilterOption.slice(pIdx + 1)]
                            }
                            
                            this.props.updatePopulationFilters([...this.props.populationFilters.slice(0, idx), 
                                    {...this.props.populationFilters[idx], 'valueFilterOption': updatedValueFilterOption},
                                    ...this.props.populationFilters.slice(idx + 1)])
                        },
                        removeEntry: () => {
                                if (this.props.viewType !== ViewType.TRIGGERS && this.props.populationFilters[idx].uuid !== populationFilterUUId) return null
                                this.props.updatePopulationFilters([...this.props.populationFilters.slice(0, idx), 
                                    {...this.props.populationFilters[idx], 'valueFilterOption': [...this.props.populationFilters[idx].valueFilterOption.slice(0, pIdx), ...this.props.populationFilters[idx].valueFilterOption.slice(pIdx + 1)]},
                                    ...this.props.populationFilters.slice(idx + 1)])
                        },
                    }} idx={pIdx} numberLeft={this.props.populationFilters[idx].valueFilterOption.length} 
                    />
                    </div>
                    {!OptionToMultipleFilters[this.props.populationFilters[idx].option] ? null : pIdx === (this.props.populationFilters[idx].valueFilterOption.length - 1) ? this._renderModifyButton(() => {
                            if (this.props.viewType !== ViewType.TRIGGERS && this.props.populationFilters[idx].uuid !== populationFilterUUId) return null
                            this.props.updatePopulationFilters([
                                ...this.props.populationFilters.slice(0, idx), 
                                {...this.props.populationFilters[idx], 'valueFilterOption': [...this.props.populationFilters[idx].valueFilterOption, {'label': '', 'id': uuid(), 'value': '', 'selected': true}]},
                                ...this.props.populationFilters.slice(idx + 1)
                            ])
                    }) : <Typography variant="largeParagraph">or</Typography>}
                    </div>
                )
            })}

        </div>)
    }

    _renderDefaultGroup(onClick: () => void, selected: boolean): JSX.Element { 
        return (<div onClick={onClick.bind(this)} className="w-fit flex flex-row p-2 cursor-pointer opacity-100"
            style={{
                'backgroundColor': selected ? '#1e3a8ad6' : 'white',
                'borderRadius': '10px',
                'color': selected ? 'white' : 'black',
                'boxShadow': 'rgba(0, 0, 0, 0.35) 0px 5px 15px'
            }}
        >
                <Typography variant="largeParagraph" color="white">
                    All Calls                
                </Typography>
            </div>)
    }

    _renderAddFilter(): JSX.Element | null {
        return <div className="flex flex-row gap-2 items-center">
            <Typography variant="largeParagraph">Customize</Typography>
            {this._renderPlusFilter()}
        </div>
    }
    

    _renderPresetElement(preset: Presets): JSX.Element {
        return <div 
        onClick={() => this.props.updatePopulationFilters(this._getPopulationFilterFromPreset(preset))} 
        className="justify-center items-center flex cursor-pointer opacity-60 hover:opacity-100 p-1 w-5 h-5 bg-white" 
        style={{
            'borderRadius': '10px',
            'boxShadow': 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
            }}>
            {PresetToSvg[preset]}
        </div>
    }

    _renderPreset(preset: Presets): JSX.Element {
        return (<Debouncer 
            key={preset}
            elementToShow={this._renderPresetElement(preset)}
            stringToShow={PresetToInformation[preset]}
        />)
    }

    _getPopulationFilterFromPreset(preset: Presets): PopulationFilter[] {
        if (preset === Presets.MEETING_BOOKED) {
            return [{'option': Option.STAGES, 'valueFilterOption': this._getDefaultFilterOption(Option.STAGES).map((v) => {return {...v, 'selected': (v.value as STAGE) === STAGE.BOOKED}})}]
        } else if (preset === Presets.OBJECTIONS) {
            return [{'option': Option.REMARK, 'metadataFilterOption': this._getDefaultMetadataFilterOption(Option.REMARK), 'valueFilterOption': this._getDefaultFilterOption(Option.REMARK).map((v) => {return {...v, 'selected': Array.from(REMARKS_OBJECTIONS.keys()).includes((v.value as Remark))}})}]
        } else if (preset === Presets.FLAGGED) {
            return [{'option': Option.FLAGGED, 'valueFilterOption': this._getDefaultFilterOption(Option.FLAGGED).map((v) => {return {...v, 'selected': FLAGGED_FILTER.IS_FLAGGED === v.value}})}]
        } else if (preset === Presets.STARRED) {
            return [{'option': Option.STARRED, 'valueFilterOption': this._getDefaultFilterOption(Option.STARRED).map((v) => {return {...v, 'selected': STAR_FILTER.IS_STARRED === v.value}})}]
        }
        return []
    }

    _renderPresets(): JSX.Element | null {
        if (this.props.diagnosticsView || !this.props.hotSelectView || this.props.populationFilters.length > 0) return null
        return <div className="flex flex-row gap-1.5 items-center">
            <Typography color={this.props.isCoachingView ? 'white' : 'grey'} variant={this._getVariant()}>
                or
            </Typography>
            {Object.values(Presets).filter((v) => this.props.seesMultipleUsers ? ![Presets.STARRED].includes(v) : ![Presets.FLAGGED].includes(v)).map((v) => this._renderPreset(v))}
        </div>
    }

    _renderStandardView(): JSX.Element | null {
        return (<div className={"w-full filter group border-transparent " + (!this.props.diagnosticsView && this.props.hotSelectView ? 'hover:border-slate-200 p-1 border-solid border-2' : '')}
        style={{
            'borderWidth': !this.props.diagnosticsView && this.props.hotSelectView ? '1px' : '0px',
            'borderRadius': !this.props.diagnosticsView && this.props.hotSelectView ? '5px' : 'initial',
        }} >
        {this.props.cannotRemoveFilter || this.props.populationFilters.length === 0 || !this.props.hotSelectView ? null : <div className="relative">
        <button
            onClick={(e) => {
                e.stopPropagation()
                this.props.updatePopulationFilters(this.props.populationFilters.filter(p => p.notRemovable))
            }}
            type="button"
            className="absolute z-50 -top-2.5 -left-2.5 h-4 w-4 items-center hidden group-hover:flex justify-center rounded-full border border-gray-200 bg-gray-100 cursor-pointer"
        >
            <div className="flex max-w-full grow">
                {React.cloneElement(xIcon, {className: "h-full w-full text-gray-500 dark:text-zinc-400"})}
            </div>
        </button>
        </div>
        }
        
        <div className="w-full flex gap-1.5 text-center" style={{
            'alignItems': this.props.hotSelectView ? 'center' : 'left',
            'flexDirection': this.props.hotSelectView ? 'row' : 'column',
            'flexWrap': this.props.hotSelectView ? 'wrap' : 'inherit',
            'justifyContent': this.props.viewType === ViewType.SIMPLE_ADD_BUTTON ? 'center' : undefined,
        }}>
        {this.props.populationFilters.map((v, idx: number) => {
        return <div key={v.uuid ?? v.option} className="flex flex-row gap-2 items-center text-center">
            {OptionToIsTextFilter[v.option] || v.metadataFilterOption || [Option.START, Option.END, Option.MIN_DURATION].includes(v.option)
            ? this._renderFilterElement(idx, false, this.props.populationFilters.length - 1 === idx)
            : this._renderCustomFilter(idx, idx === this.props.populationFilters.length - 1)}
        </div>    
        })}
        {this._filtersToBeSelected().length > 0 ? this._renderPlusFilter() : null}
        {this.props.diagnosticsView || !this.props.hotSelectView || this.props.populationFilters.length > 0 ? null : 
        <Typography style={{'whiteSpace': 'nowrap'}} color={this.props.isCoachingView ? 'white' : 'grey' } variant={this._getVariant()}>
                Choose filters
        </Typography>}
        {this._renderPresets()}
            </div>
        </div>
        )
    }

    _renderEmptyTriggerView() {
        return <div className="w-full h-full flex flex-row justify-center items-center text-center">
            <Typography variant="largeParagraph">
                Select a filter to start defining a scenario!
            </Typography>
        </div>
    }

    _orderingOfOptionId(v: string) {
        return [OptionToFilterId[Option.USER_IDS], OptionToFilterId[Option.TITLES], OptionToFilterId[Option.TIME], OptionToFilterId[Option.TEXT], OptionToFilterId[Option.REMARK]].indexOf(v)
    }

    _renderAllFilterOptions() {
        const optionsToShow =  Object.values(Option).filter((v) => this.props.canShowOption.includes(v) && this._hasDataForOption(v) && ![Option.TEXT, Option.REMARK, Option.TIME].includes(v))
        const filterIdsToShow = Array.from(new Set(optionsToShow.map((v) => OptionToFilterId[v]))).sort((a, b) => this._orderingOfOptionId(a) - this._orderingOfOptionId(b))
        let nameToSelectedMetadata: {'name': string, 'values': FilterSetNameInfo[]}[] = filterIdsToShow.map((v) => {
            const filteredOptions: FilterSetNameInfo[] = optionsToShow.filter((x) => OptionToFilterId[x] === v).map((x) => {return {'name': OptionToHumanReadable[x], 'option': x, 'disabled': this.props.populationFilters.find((z) => z.option === x) !== undefined}})
            return {'name': v, 'values': filteredOptions}
        })

        const existingTimeFilter=  this.props.populationFilters.find((v) => v.option === Option.TIME) !== undefined
        const timeToSelectedMetadata: FilterSetNameInfo[] = [
            {'name': TimeFilterType.BEFORE, 'option': Option.TIME, 'disabled': existingTimeFilter, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.TIME, undefined, undefined, undefined, TimeFilterType.BEFORE)},
            {'name': TimeFilterType.AFTER, 'option': Option.TIME,  'disabled': existingTimeFilter, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.TIME, undefined, undefined, undefined, TimeFilterType.AFTER)},
            {'name': TimeFilterType.BETWEEN, 'option': Option.TIME, 'disabled': existingTimeFilter, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.TIME, undefined, undefined, undefined, TimeFilterType.BETWEEN)},
        ]

        const phraseToSelectedMetadata: FilterSetNameInfo[] = [
            {'name': getNameForLookbackRule(Option.TEXT, undefined), 'option': Option.TEXT, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.TEXT, undefined, PartyRole.PROSPECT)},
            {'name': getNameForLookbackRule(Option.TEXT, LookbackRule.PRECEDED_BY), 'option': Option.TEXT, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.TEXT, LookbackRule.PRECEDED_BY, PartyRole.PROSPECT)},
            {'name': getNameForLookbackRule(Option.TEXT, LookbackRule.NOT_PRECEDED_BY), 'option': Option.TEXT, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.TEXT, LookbackRule.NOT_PRECEDED_BY, PartyRole.PROSPECT)},
        ]

        const remarkToSelectedMetadata: FilterSetNameInfo[] = [
            {'name': getNameForLookbackRule(Option.REMARK, undefined), 'option': Option.REMARK, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.REMARK)},
            {'name': getNameForLookbackRule(Option.REMARK, LookbackRule.PRECEDED_BY), 'option': Option.REMARK, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.REMARK, LookbackRule.PRECEDED_BY)},
            {'name': getNameForLookbackRule(Option.REMARK, LookbackRule.NOT_PRECEDED_BY), 'option': Option.REMARK, 'selectedMetadata': getDefaultMultiMetadataFilterOption(Option.REMARK, LookbackRule.NOT_PRECEDED_BY)},
        ]

        nameToSelectedMetadata = [...nameToSelectedMetadata, 
            {'name': OptionToHumanReadable[Option.TIME], 'values': timeToSelectedMetadata}, 
            {'name': OptionToHumanReadable[Option.TEXT], 'values': phraseToSelectedMetadata}, 
            {'name': OptionToHumanReadable[Option.REMARK], 'values': remarkToSelectedMetadata}]

        return ( 
        <div className="w-full flex-col gap-2.5 pt-2 pb-2" style={{'backgroundColor': '#f0f0f0', 'borderRadius': '10px'}}>
        <div className="w-full pl-2">
            <div onClick={() => this.setState((state) => {return {'expandedFilter': !state.expandedFilter}})} 
            className="w-fit cursor-pointer flex flex-row gap-2 items-center
             hover:bg-white pl-2 pt-1 pr-2 pb-1" style={{
                'borderRadius': '4px', 
                'boxShadow': 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px'}}>
            <Typography variant="largeParagraph" fontWeight={650}>
                Add Filter
            </Typography>
            {this._renderArrow(this.state.expandedFilter ?? false)}
            </div>
        </div>
        <div className="w-full flex flex-row gap-2 pl-3 pr-3 pt-1 pb-0.5" style={{
            'transition': 'max-height 0.5s cubic-bezier(0, 1, 0, 1)',
            'maxHeight': !this.state.expandedFilter ? '0px' : '1000px', 
            'overflow': 'hidden'}}>
            <FilterSet nameToSelectedMetadata={nameToSelectedMetadata} onOptionClick={this._addOption.bind(this)}/>
        </div>
        </div>)
    }

    _renderArrow(expanded: boolean) {
        return <span color='black' className="flex flex-col justify-center items-center"><span style={{
          'direction': 'ltr',
          listStyleType: 'none',
          'fontSize': '13px',
          'fontWeight': '400',
          'cursor': 'pointer',
          color: 'grey',
          'display': 'inline-block',
          'transition': 'transform 0.3s ease 0s',
          'borderRight': '2px solid currentcolor',
          'borderBottom': '2px solid currentcolor',
          'width': '5px',
          'height': '5px',
          'transform': expanded ? 'rotate(45deg)' : 'rotate(-45deg)'
        }}>
        </span></span>
    }

    _addOption(option: Option, selectedMetadata?: PlayMetadataOptions) {
        const populationFilter: PopulationFilter = {'uuid': uuid(), 'option': option, 'valueFilterOption': this._getDefaultFilterOption(option), 'multiMetadataFilterOption': selectedMetadata}
        this.props.updatePopulationFilters([...this.props.populationFilters, populationFilter])
    }


    _addPopulationFilter(option: Option) {
        this.props.updatePopulationFilters([...this.props.populationFilters, 
            {
                'uuid': uuid(), 'option': option,
                'metadataFilterOption': this._getDefaultMetadataFilterOption(option),
                'valueFilterOption': this._getDefaultFilterOption(option)
            }])
    }

    _renderMultiMetadata(populationFilter: PopulationFilter, idx: number) {
        const metadata = populationFilter.multiMetadataFilterOption
        if (!metadata) return null 
        return <div className="flex flex-row gap-1 items-center h-full">
            {[PlayMetadata.PARTY_ROLE, PlayMetadata.LOOKBACK_RULE, PlayMetadata.TIME_TYPE].map((v) => {
                if (!metadata[v]) return null
                return <Filter 
                key={v}
                filterType={FilterType.SINGLE}
                closeOnSelect={true}
                variant={'smallParagraph'}
                selectableFilterProps={
                    {
                        'usesRelativeAtParent': true,
                        'filterOptions': metadata[v] as FilterOption__STRICT[],
                        'onFilterUpdate': (updatedFilters: FilterOption[]) => {
                            if (idx >= this.props.populationFilters.length) return null
                            const populationFilterMatched = this.props.populationFilters[idx]
                            if (!populationFilterMatched.multiMetadataFilterOption) return null;
                            const selected = updatedFilters.find((v) => v.selected)
                            const updatedMultiMetadata: FilterOption__STRICT[] = [...(populationFilterMatched.multiMetadataFilterOption[v] as FilterOption__STRICT[])].map((v) => {return {...v, 'selected': selected ? v.value === selected.value : false}})
                            this.props.updatePopulationFilters([...this.props.populationFilters.slice(0, idx), {...populationFilterMatched, 'multiMetadataFilterOption': {...populationFilterMatched.multiMetadataFilterOption, [v]: updatedMultiMetadata}} 
                            ,...this.props.populationFilters.slice(idx  + 1)])
                        },
                        'headerElement': this._customFilterSelection(populationFilter, v)
                    }
                }
                /> 
            })}
        </div>
    }

    _renderRemoveButton(onClick: () => void, noBoxShadow?: boolean) {
        if (this.props.cannotRemoveFilter) return null
        return <div onClick={onClick.bind(this)} className="w-4 h-4 cursor-pointer bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 flex justify-center items-center transition-colors duration-200" style={{'borderRadius': '50%'}}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="text-red-600">
                <path d="M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </div>
    }

    _customFilterSelection(populationFilter: PopulationFilter, forPlayMetadata?: PlayMetadata) {
       let selected: string[] = []
       if (!forPlayMetadata) {
        selected = populationFilter.valueFilterOption.filter((v) => v.selected).map((v) => v.label)
        if (populationFilter.option === Option.USER_IDS && populationFilter.groupsFilterOption) {
            selected = [...populationFilter.groupsFilterOption.filter(v => v.selected).map(v => "in " + v.label), ...selected]
        }
       } else if (forPlayMetadata && populationFilter.multiMetadataFilterOption) {
        const matched = (populationFilter.multiMetadataFilterOption[forPlayMetadata] as FilterOption__STRICT[]).find((v) => v.selected)
        if (forPlayMetadata === PlayMetadata.PARTY_ROLE) {
            selected = [getNameForProspect(matched ? matched.value === NO_PARTY_ROLE ? undefined : matched.value as PartyRole : undefined)]
        } else if (forPlayMetadata === PlayMetadata.LOOKBACK_RULE) {
            selected = [getNameForLookbackRule(populationFilter.option, matched ? matched.value === NO_LOOKBACK_RULE ? undefined : matched.value as LookbackRule : undefined)]
        } else if (forPlayMetadata === PlayMetadata.TIME_TYPE) {
            selected = matched ? [matched.value] : []
        }
       }
       return this._renderCustomHeaderElement(selected)
    }

    _renderCustomHeaderElement(selected: string[]) {
        return <div className="cursor-pointer h-full w-fit pl-1.5 pr-1.5 pt-1 pb-1 flex text-center items-center justify-center bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors duration-200" style={{
            'borderRadius': '8px',
            'userSelect': 'none'
           }}>
            <Typography variant="smallParagraph" color="#374151">
                {`${selected.length === 0 ? 'None selected' : selected.length <= 2 ? selected.join(' or ') : selected.slice(0, 2).join(' or ') + " + " + `${selected.length - 2} others`}`}
            </Typography>
           </div>
    }

    _renderDurationFilterMinimal(populationFilter: PopulationFilter, idx: number) {
        const filtersWithDuration = populationFilter.valueFilterOption.filter((v) => v.selected)
        return <div className="flex flex-row gap-2 items-center">
        {filtersWithDuration.map((v, filterIdx, arr) => {
            const value = parseInt(v.value)
            return <div key={v.value} className="flex flex-row gap-2 items-center">
            <input 
            onChange={(e) => {
                let seconds = parseInt(e.currentTarget.value)
                seconds = isNaN(seconds) ? 0 : seconds
                
                // find matching label
                const matchingIdx = this.props.populationFilters[idx].valueFilterOption.findIndex((x) => x.label === v.label)
                if (matchingIdx === -1) return 
                const updatedValueFilter: FilterOption__STRICT[] = [
                    ...this.props.populationFilters[idx].valueFilterOption.slice(0, matchingIdx), 
                    {
                        ...this.props.populationFilters[idx].valueFilterOption[matchingIdx],
                        'value': seconds.toString()
                    }, 
                    ...this.props.populationFilters[idx].valueFilterOption.slice(matchingIdx + 1)
                ]
                this.props.updatePopulationFilters([
                    ...this.props.populationFilters.slice(0, idx), 
                    {...this.props.populationFilters[idx], 'valueFilterOption': updatedValueFilter},
                    ...this.props.populationFilters.slice(idx + 1)
                ])
            }}
            type="number"
            value={isNaN(value) ? 0 : value}
            min={0}
            style={
                {
                    'width': '50px',
                    'lineHeight': '0.75em',
                    'fontSize': '12px'
                }
            } 
            />
            {<Typography variant="smallParagraph">(s)</Typography>}
            {filterIdx === 0 && arr.length > 1 ? <Typography variant="smallParagraph">to</Typography> : null}
            </div>
        })}
        </div>
    }


    _renderFilterMinimalist(v: PopulationFilter, idx: number) {
        if (OptionToIsTextFilter[v.option]) return this._renderTextFilter(idx)
        else if (OptionToIsDurationFilter[v.option]) return this._renderDurationFilterMinimal(v, idx)
        else if (v.option === Option.USER_IDS) return this._renderUserPicker(v, this._customFilterSelection(v), idx)
        return <Filter 
        filterType={FilterType.MULTI}
        closeOnSelect={false}
        selectableFilterProps={
            {
                'usesRelativeAtParent': true,
                'filterOptions': v.valueFilterOption,
                'onFilterUpdate': (updatedFilters: FilterOption[]) => this._onMainFilterUpdate.bind(this)(updatedFilters, idx),
                'headerElement': this._customFilterSelection(v),
            }
        }
        variant={'smallParagraph'}
        />
    }

    _renderFilterElementMinimalist(v: PopulationFilter, idx: number) {
        return <div key={v.uuid ?? v.option} className="w-full flex flex-row gap-2 items-center">
            {this._renderRemoveButton(() => {
                this._onRemoveFilter.bind(this)(idx)
            }, true)}
            <div className="w-full flex flex-row gap-1 items-center h-full">
            <Typography variant="smallParagraph" fontWeight={600} color={idx === 0 ? 'white' : 'black'}>
                And
            </Typography>
            <Typography variant="smallParagraph" fontWeight={600}>
                {`${OptionToHumanReadable[v.option]}:`}
            </Typography>
            <div className="w-full flex flex-row gap-1 items-center h-full">
            {v.multiMetadataFilterOption ? this._renderMultiMetadata(v, idx) : null}
            {this._renderFilterMinimalist(v, idx)}
            </div>
            </div>
        </div>
    }

    _renderPopulationFiltersOption() {
        if (this.props.populationFilters.length === 0) return null
        return <div className="w-full flex flex-col gap-2 pr-3 pt-1 pb-2 ">
            {this.props.populationFilters.map((v: PopulationFilter, idx) => {
                return this._renderFilterElementMinimalist(v, idx)
            })}
        </div> 
    }

    _renderPopulationFilters() {
        return <div className="w-full flex flex-col gap-2">
            <div className="w-full flex flex-row justify-between pr-1">
            <div className="w-full flex flex-row gap-3 items-center">
            <div className="flex flex-row gap-1.5 items-center">
            <Typography variant='largeParagraph' fontWeight={650} color={this.props.populationFilters.length === 0 ? 'lightgrey' : 'black'}>
                When:
            </Typography>
            {this.props.updateConditionName ? <EditableAutosizedInput 
                        fontSize={12}
                        editFunctions={{
                            'addEntry': () => {},
                            'removeEntry': () => {},
                            'updateEntry': (idx: number, updatedEntry: string) => {
                                if (this.props.updateConditionName) this.props.updateConditionName(updatedEntry)
                            }
                        }}
                        placeholder={'Enter play name'}
                        prompt={this.props.conditionName ?? ''}
                        numberLeft={1}
                        idx={0}
                        maxWidth={"100px"}
                        minHeight={"15px"}
                    /> : null}
            </div>
            {this.props.showMatches ? <div className="flex flex-row gap-1">
                <Typography variant="largeParagraph" color="#374151">
                    Matches:
                </Typography>
                <div onClick={this.props.onMatchClick?.bind(this)} className="w-fit flex items-center bg-white border border-gray-200 hover:border-gray-300 pl-2 pr-2 hover:bg-gray-50 cursor-pointer transition-colors duration-200" style={{
                            'borderRadius': '6px'
                            }}>
                        <Typography variant="largeParagraph" color="#374151">
                            {this.props.numberMatches !== undefined ? this.props.totalPossibleMatches ? `${this.props.numberMatches} (${(this.props.numberMatches/this.props.totalPossibleMatches).toFixed(1)}%)` : this.props.numberMatches : 'Requested...'}
                        </Typography>
                </div>
            </div>: null}
            </div>
            {<div onClick={() => this.props.updatePopulationFilters([])} 
                        className={`w-fit flex items-center pl-2 pr-2 bg-white border border-gray-200 transition-colors duration-200 ${this.props.populationFilters.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 hover:border-gray-300 cursor-pointer'}`} style={{
                            'borderRadius': '6px'
                            }}>
                        <Typography variant="largeParagraph" 
                        style={{'whiteSpace': 'nowrap'}}
                        color={this.props.populationFilters.length === 0 ? '#9CA3AF' : '#374151'}>
                            Remove all
                        </Typography>
                        </div>
                }
            </div>
            {((this.props.canShowLanguageOption && this.props.languageOption && this.props.updateLanguageOption) || (this.props.stemmingOption !== undefined)) ? <div className="w-full flex flex-row gap-2 items-center">
            <Typography variant='largeParagraph' fontWeight={650} color={this.props.populationFilters.length === 0 ? 'lightgrey' : 'black'}>
                Settings:
            </Typography>
            {this.props.canShowLanguageOption && this.props.languageOption && this.props.updateLanguageOption ? 
            <div className="flex flex-row gap-1.5 items-center">
                <Typography variant="largeParagraph">
                    Language is
                </Typography>
                <Filter 
                    filterType={FilterType.SINGLE}
                    closeOnSelect={false}
                    selectableFilterProps={
                        {
                        'usesRelativeAtParent': true,
                        'filterOptions': this.props.languageOption,
                        'onFilterUpdate': (updatedFilters: FilterOption[]) => {
                            if (!this.props.updateLanguageOption) return 
                            this.props.updateLanguageOption(updatedFilters)
                            },
                        'headerElement': this._renderCustomHeaderElement(this.props.languageOption.filter((v) => v.selected).map((v) => v.label.toString())),
                        }
                }
                variant={'smallParagraph'}
                />
            </div> : null}
            {this.props.stemmingOption !== undefined ?
                <div className="flex flex-row gap-1.5 items-center">
                    <Typography variant="largeParagraph">
                        Stemming:
                    </Typography>
                    <Filter 
                    filterType={FilterType.SINGLE}
                    closeOnSelect={false}
                    selectableFilterProps={
                        {
                        'usesRelativeAtParent': true,
                        'filterOptions': [{'label': 'Yes', 'value': "true", 'selected': this.props.stemmingOption}, {'label': 'No', 'value': 'false', 'selected': !this.props.stemmingOption}],
                        'onFilterUpdate': (updatedFilters: FilterOption[]) => {
                            if (!this.props.updateStemmingOption) return
                            const selected = updatedFilters.find((v) => v.selected)
                            if (!selected) return
                            const selectedValue = selected.value === 'true'
                            this.props.updateStemmingOption(selectedValue)
                            },
                        'headerElement': this._renderCustomHeaderElement(this.props.stemmingOption ? ['Yes'] : ['No']),
                        }
                }
                variant={'smallParagraph'}
                />
                </div> : null
            }
            </div> : null}
            {this._renderPopulationFiltersOption()}
        </div>
    }

    _renderTriggersView() {
        return <div className="bg-white w-full h-fit max-h-full flex flex-col gap-2 p-2 justify-between border border-gray-200 hover:border-gray-300 transition-colors duration-200" style={{
            'borderRadius': '8px',
        }}>
            {this._renderPopulationFilters()}
            {this._renderAllFilterOptions()}            
        </div>
    }

    render() {
        if (this.props.viewType === ViewType.TRIGGERS) return this._renderTriggersView()
        return this._renderStandardView()  
    }
}

export const FilterSelector = connect((state: RootState) => {
    const assignedUserIds = new Set(state.userGroupInfo.sortedGroupInfo.map(g => g.allUserIds).flat(1))
    return {
        seesMultipleUsers: state.visibleAccounts.value?.users !== undefined && state.visibleAccounts.value.users.length > 1,
        hasUnassignedUsers: state.visibleAccounts.value?.users.some(u => !assignedUserIds.has(u.user_id)) ?? false,
        dispositionOptions: state.dispositionOptions.value,
        prospectInfoOptions: state.prospectInfoOptions.value,
    }
}, null, null, {forwardRef: true})(FilterSelectorImpl)

type FilterSetNameInfo = {'name': string, 'selectedMetadata'?: PlayMetadataOptions, 'option': Option, 'disabled'?: boolean}

type FilterSetProps = {
    nameToSelectedMetadata: {'name': string, 'values': FilterSetNameInfo[]}[]
    onOptionClick: (selected: Option, selectedMetaData?: PlayMetadataOptions) => void
}

class FilterSet extends React.Component<FilterSetProps, {}> {
    constructor(props: FilterSetProps) {
        super(props)
    }

    _renderIndividualFilter(filter: {'name': string, values: FilterSetNameInfo[]}) {
        return <div key={filter.name} className="flex flex-col gap-2" style={{'width': `${Math.floor(100/this.props.nameToSelectedMetadata.length)}%`}}>
            <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                {filter.name}
            </Typography>
            {filter.values.map((x: FilterSetNameInfo) => {
                return <div key={x.name} onClick={() => {if (!x.disabled) this.props.onOptionClick(x.option, x.selectedMetadata)}}
                className={`text-center w-full p-1.5 border transition-colors duration-200 ${x.disabled ? "bg-gray-100 border-gray-200 cursor-not-allowed" : "cursor-pointer bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300"}`} style={{
                    'borderRadius' : '6px',
                    'lineHeight': '14px'
                }}>
                <Typography variant="smallParagraph" color={x.disabled ? '#9CA3AF' : '#374151'}>
                    {x.name}
                </Typography>
            </div>
            })}
        </div>
    }

    render() {
        return <div className="w-full flex flex-row gap-2 text-center">
            {this.props.nameToSelectedMetadata.map((v) => this._renderIndividualFilter(v))}
        </div>
    }
}